import { Component, ElementRef, Renderer2, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { AppTopbar } from './topbar/app.topbar';
import { AppSidebar } from './sidebar/app.sidebar';
import { LayoutService } from '../service/layout.service';
import { ComplianceRequestsDialogComponent } from '@/shared/components/compliance-requests-dialog/compliance-requests-dialog.component';
import { DealPreferencesPromptDialogComponent } from '@/shared/components/deal-preferences-prompt-dialog/deal-preferences-prompt-dialog.component';
import { DrawerModule } from 'primeng/drawer';
import { ButtonModule } from 'primeng/button';
import { NotificationsLiveService } from '@/pages/notifications/notifications-live.service';
import { AiChatDrawerComponent } from '@/pages/ai-chat/components/ai-chat-drawer/ai-chat-drawer.component';
import { AI_FEATURE_ENABLED } from '@/shared/utilities/feature-flags';
import { TalkToUsWidgetComponent } from '@/pages/questions/components/talk-to-us-widget/talk-to-us-widget.component';
import { FeedbackWidgetComponent } from '@/pages/feedback/components/feedback-widget/feedback-widget.component';

@Component({
    selector: 'app-layout',
    standalone: true,
    imports: [CommonModule, AppTopbar, AppSidebar, RouterModule, ComplianceRequestsDialogComponent, DealPreferencesPromptDialogComponent, DrawerModule, ButtonModule, AiChatDrawerComponent, TalkToUsWidgetComponent, FeedbackWidgetComponent],
    template: `<div #layoutBg class="layout-wrapper-background grid grid-cols-12 min-h-screen w-full overflow-hidden gap-(--grid-layout-gutter) p-(--grid-layout-padding)" [ngClass]="containerClass">
        <p-drawer
            class="contents"
            [visible]="layoutService.layoutState().staticMenuMobileActive ?? false"
            (visibleChange)="onMobileDrawerVisibleChange($event)"
            position="left"
            [modal]="true"
            [dismissible]="true"
            [closable]="false"
            [closeOnEscape]="true"
            [blockScroll]="true"
            [appendTo]="'body'"
            [style]="{ width: '20rem', maxWidth: '85vw' }"
            styleClass="lg:hidden !border-0 !bg-transparent !shadow-none"
        >
            <ng-template #headless>
                <div class="layout-sidebar mobile-drawer-sidebar relative h-full overflow-y-auto hide-scrollbar rounded-r-12 px-2.5 py-5 shadow-lg" [style.background-color]="'rgb(242, 234, 224)'">
                    <button pButton type="button" icon="pi pi-times" class="absolute right-4 top-4 z-10 lg:hidden" [text]="true" aria-label="Close navigation menu" (click)="hideMenu()"></button>
                    <app-sidebar></app-sidebar>
                </div>
            </ng-template>
        </p-drawer>
        <div class="layout-sidebar-wrapper hidden lg:block lg:col-span-2 h-[calc(100vh-64px)] overflow-y-auto hide-scrollbar">
            <app-sidebar></app-sidebar>
        </div>
        <div class="layout-content-wrapper col-span-12 lg:col-span-10 flex flex-col gap-(--spacing-layout-l)">
            <div class="layout-content-wrapper col-span-12 lg:col-span-10 flex flex-col gap-(--spacing-layout-l)">
                <div class="flex flex-col justify-between bg-transparentr">
                    <div class="flex flex-col bg-transparent h-[calc(100vh-64px)] overflow-y-auto hide-scrollbar" #scrollContainer>
                        <app-topbar></app-topbar>
                        <router-outlet></router-outlet>
                        <!-- <app-footer></app-footer> -->
                    </div>
                </div>
                <!-- <div class="layout-mask animate-fadein"></div> -->
            </div>
        </div>
        <app-compliance-requests-dialog />
        <app-deal-preferences-prompt-dialog />
        @if (aiFeatureEnabled) {
        <app-ai-chat-drawer />
        }
        <app-talk-to-us-widget />
        <app-feedback-widget />
    </div> `
})
export class AppLayout {
    protected readonly aiFeatureEnabled = AI_FEATURE_ENABLED;

    overlayMenuOpenSubscription: Subscription;

    menuOutsideClickListener: any;

    private scrollRAF: number | null = null;

    @ViewChild(AppSidebar) appSidebar!: AppSidebar;

    @ViewChild(AppTopbar) appTopBar!: AppTopbar;

    @ViewChild('scrollContainer', { static: true })
    scrollContainer!: ElementRef<HTMLDivElement>;
    @ViewChild('layoutBg', { static: true })
    layoutBg!: ElementRef<HTMLDivElement>;

    constructor(
        public layoutService: LayoutService,
        public renderer: Renderer2,
        public router: Router,
        private readonly notificationsLiveService: NotificationsLiveService
    ) {
        // Open the live notifications stream for the whole authenticated session, so
        // new notifications (and their corner pop-ups) arrive on any page, not just /notifications.
        this.notificationsLiveService.connect();

        this.overlayMenuOpenSubscription = this.layoutService.overlayOpen$.subscribe(() => {
            if (!this.menuOutsideClickListener) {
                this.menuOutsideClickListener = this.renderer.listen('document', 'click', (event) => {
                    if (this.isOutsideClicked(event)) {
                        this.hideMenu();
                    }
                });
            }

            if (this.layoutService.layoutState().staticMenuMobileActive) {
                this.blockBodyScroll();
            }
        });

        this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
            this.hideMenu();
        });
    }

    ngAfterViewInit() {
        const scrollEl = this.scrollContainer.nativeElement;
        const bgEl = this.layoutBg.nativeElement;

        const updateBg = () => {
            const contentHeight = scrollEl.scrollHeight;
            bgEl.style.setProperty('--bg-height', `${contentHeight}px`);
        };

        // initial sync
        updateBg();

        // scroll movement - parallax effect
        scrollEl.addEventListener(
            'scroll',
            () => {
                const y = scrollEl.scrollTop;
                const maxScroll = scrollEl.scrollHeight - scrollEl.clientHeight;

                // how much % user scrolled (0 → 1)
                const progress = maxScroll > 0 ? Math.min(1, Math.max(0, y / maxScroll)) : 0;

                // background total travel distance
                const MAX_BG_MOVE = 420; // px (tweakable)
                const shift = progress * MAX_BG_MOVE;
                bgEl.style.setProperty('--bg-shift', `${-shift}px`);

                // green gradient travel (also ratio based)
                const greenStart = 72;
                const greenEnd = 25;
                const greenPos = greenStart - progress * (greenStart - greenEnd);
                bgEl.style.setProperty('--green-shift', `${greenPos}%`);
            },
            { passive: true }
        );

        // content changes / resize
        window.addEventListener('resize', updateBg, { passive: true });

        const handler = () => {
            if (this.scrollRAF) cancelAnimationFrame(this.scrollRAF);
            this.scrollRAF = requestAnimationFrame(() => this.updateSidebarTint());
        };

        window.addEventListener('scroll', handler, { passive: true });
        window.addEventListener('resize', handler, { passive: true });
        handler();
    }

    onMobileDrawerVisibleChange(visible: boolean) {
        this.layoutService.layoutState.update((prev) => ({
            ...prev,
            staticMenuMobileActive: visible
        }));
    }

    private updateSidebarTint() {
        const color = this.sampleBackdropColor();
        const withAlpha = this.applyAlpha(color ?? 'rgb(240, 245, 240)', 0.28);
        document.documentElement.style.setProperty('--sidebar-dynamic-color', withAlpha);
    }

    private sampleBackdropColor(): string | null {
        try {
            const sidebar = document.querySelector('.layout-sidebar') as HTMLElement | null;
            const right = sidebar ? sidebar.getBoundingClientRect().right : 0;
            const x = Math.round(right + 12);
            const y = Math.round(window.innerHeight / 2);
            let el = document.elementFromPoint(x, y) as HTMLElement | null;
            let limit = 10;
            while (el && limit-- > 0) {
                const cs = getComputedStyle(el);
                const bg = cs.backgroundColor;
                if (bg && bg !== 'transparent' && !bg.includes('rgba(0, 0, 0, 0)')) return bg;
                const bi = cs.backgroundImage;
                if (bi && bi !== 'none' && /gradient/i.test(bi)) {
                    const rect = el.getBoundingClientRect();
                    const t = this.positionRatioWithin(x, y, rect, bi);
                    const colors = this.extractColorsFromGradient(bi);
                    if (colors.length >= 2) {
                        const mixed = this.mix(colors[0], colors[1], t);
                        return `rgba(${mixed.r}, ${mixed.g}, ${mixed.b}, 1)`;
                    }
                }
                el = el.parentElement;
            }
        } catch {}
        return null;
    }

    private positionRatioWithin(x: number, y: number, rect: DOMRect, gradient: string): number {
        // Default linear-gradient direction is top-to-bottom; handle 0deg/90deg/180deg/270deg and 135deg used by us
        const angleMatch = gradient.match(/linear-gradient\(([^,]+),/i);
        let angle = 180; // CSS default is to bottom (180deg)
        if (angleMatch) {
            const a = angleMatch[1].trim();
            const degMatch = a.match(/(-?\d+(?:\.\d+)?)deg/i);
            if (degMatch) angle = parseFloat(degMatch[1]);
            else if (/to right/i.test(a)) angle = 90;
            else if (/to left/i.test(a)) angle = 270;
            else if (/to top/i.test(a)) angle = 0;
            else if (/to bottom/i.test(a)) angle = 180;
        }

        // Compute projection of point along gradient axis (rough approximation)
        const rad = (angle * Math.PI) / 180;
        const ux = Math.cos(rad),
            uy = Math.sin(rad);
        const px = x - rect.left,
            py = y - rect.top;
        const startX = 0,
            startY = 0;
        const endX = rect.width * ux + rect.height * 0 * uy; // simplified for linear mapping
        const endY = rect.height * uy + rect.width * 0 * ux;
        const projLen = ux * px + uy * py;
        const maxLen = Math.max(1, ux * rect.width + uy * rect.height);
        const t = Math.min(1, Math.max(0, projLen / maxLen));
        return t;
    }

    private extractColorsFromGradient(gradient: string): Array<{ r: number; g: number; b: number }> {
        const colors: Array<{ r: number; g: number; b: number }> = [];
        const regex = /rgba?\((\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*(\d*\.?\d+))?\)/gi;
        let m: RegExpExecArray | null;
        while ((m = regex.exec(gradient)) !== null) {
            colors.push({ r: Number(m[1]), g: Number(m[2]), b: Number(m[3]) });
        }
        return colors;
    }

    private mix(a: { r: number; g: number; b: number }, b: { r: number; g: number; b: number }, t: number) {
        const cl = (x: number) => Math.round(Math.max(0, Math.min(255, x)));
        return { r: cl(a.r + (b.r - a.r) * t), g: cl(a.g + (b.g - a.g) * t), b: cl(a.b + (b.b - a.b) * t) };
    }

    private applyAlpha(color: string, alpha: number): string {
        const m = color.match(/rgba?\((\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*(\d*\.?\d+))?\)/i);
        if (m) {
            const r = Number(m[1]);
            const g = Number(m[2]);
            const b = Number(m[3]);
            return `rgba(${r}, ${g}, ${b}, ${alpha})`;
        }
        return color;
    }

    isOutsideClicked(event: MouseEvent) {
        const sidebarEl = document.querySelector('.layout-sidebar');
        const topbarEl = document.querySelector('.layout-menu-button');
        const eventTarget = event.target as Node;

        return !(sidebarEl?.isSameNode(eventTarget) || sidebarEl?.contains(eventTarget) || topbarEl?.isSameNode(eventTarget) || topbarEl?.contains(eventTarget));
    }

    hideMenu() {
        this.layoutService.layoutState.update((prev) => ({ ...prev, overlayMenuActive: false, staticMenuMobileActive: false, menuHoverActive: false }));
        if (this.menuOutsideClickListener) {
            this.menuOutsideClickListener();
            this.menuOutsideClickListener = null;
        }
        this.unblockBodyScroll();
    }

    blockBodyScroll(): void {
        if (document.body.classList) {
            document.body.classList.add('overflow-hidden');
        } else {
            document.body.className += ' overflow-hidden';
        }
    }

    unblockBodyScroll(): void {
        if (document.body.classList) {
            document.body.classList.remove('overflow-hidden');
        } else {
            document.body.className = document.body.className.replace(new RegExp('(^|\\b)' + 'overflow-hidden'.split(' ').join('|') + '(\\b|$)', 'gi'), ' ');
        }
    }

    get containerClass() {
        return {
            'layout-overlay': this.layoutService.layoutConfig().menuMode === 'overlay',
            'layout-static': this.layoutService.layoutConfig().menuMode === 'static',
            'layout-static-inactive': this.layoutService.layoutState().staticMenuDesktopInactive && this.layoutService.layoutConfig().menuMode === 'static',
            'layout-overlay-active': this.layoutService.layoutState().overlayMenuActive,
            'layout-mobile-active': this.layoutService.layoutState().staticMenuMobileActive
        };
    }

    ngOnDestroy() {
        this.notificationsLiveService.disconnect();

        if (this.overlayMenuOpenSubscription) {
            this.overlayMenuOpenSubscription.unsubscribe();
        }

        if (this.menuOutsideClickListener) {
            this.menuOutsideClickListener();
        }

        if (this.scrollRAF) {
            cancelAnimationFrame(this.scrollRAF);
        }
    }
}
